import { FormikValues } from 'formik';
import { useSnackbar } from 'notistack';
import { useState } from 'react';

import { useMemberOperatorTaskContext } from 'modules/common/components/MemberOperator';
import { TaskStatus } from 'modules/common/components/MemberOperator/MemberOperator.interfaces';
import { FieldTypes, FormField, SubmitActionTask } from 'modules/Tasks/types';
import useInvalidateMemberTaskQueries from 'services/memberTasks/useInvalidateMemberTaskQueries';
import { sendDatadogRumError } from 'utils/analytics/datadogRum';

interface UseTaskFormSubmitArgs {
    task: SubmitActionTask;
    fields: FormField[];
    onClose: () => void;
}

/**
 * Owns the FormModal's state machine + side effects so the component stays a thin renderer.
 * Three concerns: draft persistence (localStorage), derived initial state, and user actions.
 */
export default function useTaskFormSubmit({ task, fields, onClose }: UseTaskFormSubmitArgs) {
    const { updateTaskStatus } = useMemberOperatorTaskContext();
    const invalidateMemberTaskQueries = useInvalidateMemberTaskQueries();
    const { enqueueSnackbar } = useSnackbar();
    const draftKey = `formDraft:${task.id}`;

    // ---- Draft persistence ----
    // Read once on mount, write on Next/dismiss, clear on submit success.

    const [savedDraft] = useState<{ currentPage: number; responses: FormikValues } | null>(() => {
        try {
            return JSON.parse(globalThis.localStorage.getItem(draftKey) ?? 'null');
        } catch {
            return null;
        }
    });

    const saveDraft = (page: number, values: FormikValues) => {
        try {
            globalThis.localStorage.setItem(draftKey, JSON.stringify({ currentPage: page, responses: values }));
        } catch (error) {
            sendDatadogRumError('Form task draft save failed', error, { taskId: task.id });
        }
    };

    const clearDraft = () => globalThis.localStorage.removeItem(draftKey);

    // ---- Initial state (seeded from the saved draft when present) ----

    const totalPages = fields.length;

    const [currentPage, setCurrentPage] = useState(Math.max(0, Math.min(savedDraft?.currentPage ?? 0, totalPages - 1)));

    const initialResponses =
        savedDraft?.responses ??
        Object.fromEntries(fields.map((f) => [f.key, f.field_type === FieldTypes.MULTISELECT ? [] : '']));

    // ---- User actions ----

    const next = (values: FormikValues) => {
        saveDraft(currentPage + 1, values);
        setCurrentPage((p) => Math.min(p + 1, totalPages - 1));
    };

    const back = () => setCurrentPage((p) => Math.max(p - 1, 0));

    const close = (values: FormikValues) => {
        saveDraft(currentPage, values);
        onClose();
    };

    const handleSubmit = (values: FormikValues, { setSubmitting }: { setSubmitting: (b: boolean) => void }) => {
        updateTaskStatus(
            {
                status: TaskStatus.COMPLETED,
                execute_handlers_async: true,
                completion_payload: { responses: values },
            },
            {
                onSuccess: () => {
                    clearDraft();
                    invalidateMemberTaskQueries(task);
                    onClose();
                },
                onError: (error) => {
                    setSubmitting(false);
                    enqueueSnackbar('Something went wrong submitting your answers. Please try again.', {
                        variant: 'error',
                    });
                    sendDatadogRumError('Form task submission failed', error, { taskId: task.id });
                },
            }
        );
    };

    return { currentPage, totalPages, initialResponses, next, back, close, handleSubmit };
}
